import { Icon } from "@pepperstone/pepper-ui-kit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  CashierTradingAccount,
  CashierTransactionView,
} from "@/api/cashier";
import { useCurrencies } from "@/hooks/deposit/useCurrencies";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import { TransactionHistoryPlatformDetails } from "../transaction-history-table/TransactionHistoryPlatformDetails";
import { TransactionHistorySavedPaymentDetails } from "../transaction-history-table/TransactionHistorySavedPaymentDetails";
import { TransactionHistoryTypeLabel } from "../transaction-history-table/TransactionHistoryTypeLabel";
import { TransactionStatusBadge } from "../transaction-history-table/TransactionStatusBadge";
import styles from "./TransactionHeader.module.css";
import { TransactionHeaderLoadingSkeleton } from "./TransactionHeaderLoadingSkeleton";
import { moneyToMajorUnits } from "@/utils/helpers";

/* The TransactionHeader component displays the header information for a specific transaction
    @param transaction - The transaction data to be displayed in the header
    @param tradingAccounts - Optional list of trading accounts for additional details
    @param isLoading - Flag indicating if the transaction data is still loading
    @param clickEvent - Optional click event handler for the back button
    @returns A ReactElement representing the transaction header.
*/
export const TransactionHeader = ({
  transaction,
  tradingAccounts,
  isLoading = false,
  clickEvent,
}: {
  transaction: CashierTransactionView;
  tradingAccounts?: CashierTradingAccount[];
  isLoading?: boolean;
  clickEvent?: () => void;
}) => {
  const { t } = useTranslation();
  const { currencies } = useCurrencies();
  const { formatAmount } = useFormatAmount();

  const transactionAmount = useMemo(() => {
    return formatAmount(
      moneyToMajorUnits(transaction.money),
      currencies?.[transaction.money.code]
    );
  }, [currencies, formatAmount, transaction.money, transaction.money.code]);

  const fromTransfer =
    transaction.type === "transfer" && transaction.from_trading_account ? (
      <TransactionHistoryPlatformDetails
        tradingAccountId={transaction.from_trading_account}
        tradingAccounts={tradingAccounts}
      />
    ) : (
      t("transactions.history.table.admin")
    );

  const toTransfer =
    transaction.type === "transfer" && transaction.to_trading_account ? (
      <TransactionHistoryPlatformDetails
        tradingAccountId={transaction.to_trading_account}
        tradingAccounts={tradingAccounts}
      />
    ) : null;

  const fromDeposit =
    transaction.type === "deposit" && transaction.saved_payment_account ? (
      <TransactionHistorySavedPaymentDetails
        savedPaymentAccountData={transaction.saved_payment_account}
      />
    ) : (
      "-"
    );

  //this probably should have been placed in the to field
  const toDeposit =
    transaction.type === "deposit" && transaction.from_trading_account ? (
      <TransactionHistoryPlatformDetails
        tradingAccountId={transaction.from_trading_account}
        tradingAccounts={tradingAccounts}
      />
    ) : null;

  const fromWithdrawal =
    transaction.type === "withdrawal" && transaction.from_trading_account ? (
      <TransactionHistoryPlatformDetails
        tradingAccountId={transaction.from_trading_account}
        tradingAccounts={tradingAccounts}
      />
    ) : null;

  const toWithdrawal =
    transaction.type === "withdrawal" && transaction.saved_payment_account ? (
      <TransactionHistorySavedPaymentDetails
        savedPaymentAccountData={transaction.saved_payment_account}
      />
    ) : (
      "-"
    );

  const fromCashAdjust =
    transaction.type === "cashAdjustment"
      ? t("transactions.history.table.admin")
      : null;
  const toCashAdjust =
    transaction.type === "cashAdjustment" ? (
      <TransactionHistoryPlatformDetails
        tradingAccountId={transaction.to_trading_account}
        tradingAccounts={tradingAccounts}
      />
    ) : null;

  return (
    <div className={styles.transactionHeaderContainer}>
      <button type="button" className={styles.backButton} onClick={clickEvent}>
        <Icon icon="arrow-left" size={12} />
      </button>
      {isLoading ? (
        <TransactionHeaderLoadingSkeleton />
      ) : (
        <>
          <h2 className={styles.transactionHeaderTitle}>
            <TransactionHistoryTypeLabel type={transaction.type} />
          </h2>
          <div className={styles.transactionAmountandStatus}>
            <span className={styles.transactionAmount}>
              {transactionAmount}
            </span>
            <TransactionStatusBadge status={transaction.status} />
          </div>
          <div className={styles.transactionDetails}>
            <div className={styles.fromAccount}>
              <p>{t("transactions.history.details.from")}</p>
              {transaction.type === "deposit" ? fromDeposit : null}
              {transaction.type === "transfer" ? fromTransfer : null}
              {transaction.type === "withdrawal" ? fromWithdrawal : null}
              {transaction.type === "cashAdjustment" ? fromCashAdjust : null}
            </div>
            <div className={styles.toAccount}>
              <p>{t("transactions.history.details.to")}</p>
              {transaction.type === "deposit" ? toDeposit : null}
              {transaction.type === "transfer" ? toTransfer : null}
              {transaction.type === "withdrawal" ? toWithdrawal : null}
              {transaction.type === "cashAdjustment" ? toCashAdjust : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
